import type { ApolloClient, InMemoryCache } from '@apollo/client';

/** Single cast point — Apollo's client.cache is always InMemoryCache in our setup */
export function getInMemoryCache(client: ApolloClient): InMemoryCache {
  return client.cache as InMemoryCache;
}

interface FolderLike {
  path?: string;
  name: string;
}

/**
 * Compute a folder's full path from its parent path + name.
 * Matches the convention used by the backend and isItemInsideFolder().
 */
export function getFolderFullPath(folder: FolderLike): string {
  return folder.path ? `${folder.path}/${folder.name}` : `/${folder.name}`;
}

/**
 * Look up a Group (folder) in the Apollo normalized cache by its _id.
 * Returns the full folder path if found, or null if already evicted.
 *
 * Accepts an optional pre-extracted cache snapshot to avoid redundant
 * cache.extract() calls when the caller also needs the snapshot.
 */
export function getFolderPathFromCache(
  cache: InMemoryCache,
  folderId: string,
  snapshot?: Record<string, unknown>,
): string | null {
  const cacheId = cache.identify({ __typename: 'Group', _id: folderId });
  if (!cacheId) return null;

  const data = snapshot ?? cache.extract();
  const entry = data[cacheId] as Record<string, unknown> | undefined;
  if (!entry || typeof entry.name !== 'string') return null;

  return getFolderFullPath({ path: entry.path as string | undefined, name: entry.name });
}

/**
 * Evict all normalized cache entities whose `path` field is inside the
 * deleted folder. This mirrors the backend's cascading folder deletion:
 * everything inside the folder (items, sub-folders) is removed from cache.
 *
 * `typenames` specifies which entity types to evict (e.g., `['Playlist', 'Group']`
 * for a playlist folder deletion). Every folder has an associated type, so callers
 * must always provide the correct typenames to avoid evicting unrelated entities.
 *
 * Accepts an optional pre-extracted cache snapshot to avoid redundant
 * cache.extract() calls when the caller already has one.
 *
 * @returns number of evicted entities
 */
export function evictFolderContents(
  cache: InMemoryCache,
  folderPath: string,
  typenames: string[],
  snapshot?: Record<string, unknown>,
): number {
  const data = snapshot ?? cache.extract();
  let count = 0;

  for (const [cacheKey, entry] of Object.entries(data)) {
    // Skip ROOT_QUERY, ROOT_MUTATION, and non-entity entries
    if (!entry || typeof entry !== 'object' || !('__typename' in entry)) continue;

    const typename = (entry as Record<string, unknown>).__typename as string;
    if (!typenames.includes(typename)) continue;

    const entityPath = (entry as Record<string, unknown>).path;
    if (typeof entityPath !== 'string') continue;

    // Entity lives inside the deleted folder if its path equals or descends from folderPath
    if (entityPath === folderPath || entityPath.startsWith(`${folderPath}/`)) {
      cache.evict({ id: cacheKey });
      count++;
    }
  }

  if (count > 0) {
    cache.gc();
  }

  return count;
}
